// Receipt helper utilities to reduce code duplication
import t from 'virtual:i18n';

/**
 * Recursively removes __typename properties from GraphQL objects
 */
export function stripTypename(obj) {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
        obj.forEach(stripTypename);
    } else {
        delete obj.__typename;
        Object.values(obj).forEach(stripTypename);
    }
}

/**
 * Creates a deep copy of itemizedData and strips __typename
 */
export function cloneItemizedData(itemizedData) {
    const clone = JSON.parse(JSON.stringify(itemizedData));
    stripTypename(clone);
    return clone;
}

/**
 * Creates a FAB (Floating Action Button) element with consistent styling
 */
export function createFAB({ text, icon, onClick }) {
    const fab = document.createElement('button');
    fab.className = 'receipt-fab';

    Object.assign(fab.style, {
        position: 'absolute',
        bottom: '24px',
        right: '24px',
        display: 'flex',
        alignItems: 'center',
        padding: '16px 24px',
        borderRadius: '16px',
        border: 'none',
        background: '#d0bcff',
        color: '#1D192B',
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
        fontWeight: 'bold',
        zIndex: '1000',
        cursor: 'pointer',
        gap: '8px',
        transform: 'none',
        left: 'auto'
    });

    fab.innerHTML = `<span>${text}</span>${icon}`;
    fab.onclick = onClick;

    return fab;
}

/**
 * Standard user-plus icon SVG
 */
export const USER_PLUS_ICON = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="feather feather-user-plus" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="8.5" cy="7" r="4"></circle>
        <line x1="20" y1="8" x2="20" y2="14"></line>
        <line x1="23" y1="11" x2="17" y2="11"></line>
    </svg>
`;

/**
 * Standard checkmark icon SVG
 */
export const CHECK_ICON = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="feather feather-check" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
`;

/**
 * Creates a participant row for assignment modals
 */
export function createParticipantRow({ name, index, isSelected, onToggle }) {
    const row = document.createElement('div');
    row.className = 'assignment-row';

    Object.assign(row.style, {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        border: '1px solid transparent',
        backgroundColor: isSelected ? '#f3effc' : 'transparent',
        borderColor: isSelected ? '#d0bcff' : 'transparent'
    });

    // Avatar
    const avatar = document.createElement('div');
    Object.assign(avatar.style, {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '16px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#555'
    });
    avatar.textContent = name.charAt(0).toUpperCase();

    // Name
    const nameEl = document.createElement('div');
    nameEl.style.flex = '1';
    nameEl.style.fontSize = '16px';
    nameEl.style.fontWeight = '500';
    nameEl.textContent = name;

    // Checkmark
    const check = document.createElement('div');
    check.innerHTML = '✓';
    Object.assign(check.style, {
        color: 'var(--color-primary)',
        fontSize: '20px',
        fontWeight: 'bold',
        visibility: isSelected ? 'visible' : 'hidden'
    });

    row.appendChild(avatar);
    row.appendChild(nameEl);
    row.appendChild(check);

    row.onclick = () => onToggle(row, check);

    return { row, check };
}

/**
 * Creates the "Add new person" row
 */
export function createAddPersonRow(onAdd, label = t.addNewPerson) {
    const addRow = document.createElement('div');

    const resetRow = () => {
        Object.assign(addRow.style, {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            cursor: 'pointer',
            color: 'var(--color-primary)',
            fontWeight: '600',
            marginTop: '8px'
        });

        addRow.innerHTML = `
            <div style="width: 40px; height: 40px; border-radius: 50%; border: 2px dashed #ccc; display: flex; align-items: center; justify-content: center; margin-right: 16px; color: #888;">+</div>
            <div>${label}</div>
        `;

        addRow.onclick = () => enableEditMode();
    };

    const enableEditMode = () => {
        addRow.innerHTML = '';

        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.width = '100%';
        container.style.alignItems = 'center';

        const input = document.createElement('input');
        input.placeholder = t.name;
        Object.assign(input.style, {
            flex: '1',
            padding: '10px',
            fontSize: '16px',
            borderRadius: '8px',
            border: '1px solid var(--color-primary)',
            outline: 'none'
        });

        const saveBtn = document.createElement('button');
        saveBtn.textContent = t.add;
        Object.assign(saveBtn.style, {
            marginLeft: '10px',
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer'
        });

        const cancelBtn = document.createElement('button');
        cancelBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        `;
        Object.assign(cancelBtn.style, {
            marginLeft: '8px',
            padding: '8px',
            background: 'none',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s, color 0.2s'
        });
        cancelBtn.onmouseenter = () => {
            cancelBtn.style.backgroundColor = '#f5f5f5';
            cancelBtn.style.color = '#dc3545'; // turn red on hover
        };
        cancelBtn.onmouseleave = () => {
            cancelBtn.style.backgroundColor = 'transparent';
            cancelBtn.style.color = '#888';
        };

        container.appendChild(input);
        container.appendChild(saveBtn);
        container.appendChild(cancelBtn);
        addRow.appendChild(container);

        setTimeout(() => input.focus(), 50);

        const performAdd = () => {
            const newName = input.value.trim();
            if (!newName) return;
            onAdd(newName);
            resetRow();
        };

        saveBtn.onclick = (e) => { e.stopPropagation(); performAdd(); };
        cancelBtn.onclick = (e) => { e.stopPropagation(); resetRow(); };
        input.onkeydown = (e) => { 
            if (e.key === 'Enter') { e.stopPropagation(); performAdd(); }
            if (e.key === 'Escape') { e.stopPropagation(); resetRow(); }
        };
    };

    resetRow();
    return addRow;
}

/**
 * Compares two participant arrays for equality (order-independent)
 */
export function areParticipantsEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    const s1 = [...arr1].sort();
    const s2 = [...arr2].sort();
    return s1.every((val, i) => val === s2[i]);
}

/**
 * Creates a standard form input wrapper with label
 */
export function createFormInput({ label, type = 'text', value = '', placeholder = '', className = 'participant-name-input', ...attrs }) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${label}</label>`;

    const input = document.createElement('input');
    input.type = type;
    input.className = className;
    input.value = value;
    input.placeholder = placeholder;

    Object.assign(input, attrs);
    wrapper.appendChild(input);

    return { wrapper, input };
}

/**
 * Creates a payer selector item (matches simple expense style)
 */
export function createPayerItem({ name, index, isSelected, onClick }) {
    const row = document.createElement('div');
    row.style.cssText = `
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        cursor: pointer;
        border-bottom: 1px solid #F3F0F4;
        transition: background-color 0.15s ease;
        ${isSelected ? 'background-color: rgba(var(--color-primary-rgb), 0.07);' : ''}
    `;
    
    row.addEventListener('mouseenter', () => {
        if (!isSelected) row.style.backgroundColor = 'rgba(var(--color-primary-rgb), 0.04)';
    });
    row.addEventListener('mouseleave', () => {
        row.style.backgroundColor = isSelected ? 'rgba(var(--color-primary-rgb), 0.07)' : '';
    });

    // Avatar
    const avatar = document.createElement('div');
    avatar.style.cssText = `
        width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
        background-color: ${isSelected ? 'var(--color-primary)' : '#e8e8e8'};
        color: ${isSelected ? 'white' : '#555'};
        display: flex; align-items: center; justify-content: center;
        font-weight: 600; font-size: 16px;
    `;
    avatar.textContent = name.charAt(0).toUpperCase();

    // Name
    const nameEl = document.createElement('div');
    nameEl.style.cssText = `flex: 1; font-size: 16px; font-weight: ${isSelected ? '600' : '400'}; color: #1D1B20;`;
    nameEl.textContent = name;

    // Checkmark
    const checkEl = document.createElement('div');
    checkEl.style.cssText = `
        color: var(--color-primary); font-size: 20px; font-weight: bold;
        visibility: ${isSelected ? 'visible' : 'hidden'};
    `;
    checkEl.textContent = '✓';

    row.appendChild(avatar);
    row.appendChild(nameEl);
    row.appendChild(checkEl);

    row.addEventListener('click', onClick);
    return row;
}
